// Small browser hooks the pages share.
import { type RefObject, useEffect, useState } from "react";

/** The element's content size, kept current as it resizes. */
export function useElementSize(ref: RefObject<HTMLElement | null>): { width: number; height: number } {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

/** Whether the element is on screen (true where IntersectionObserver is missing). */
export function useVisible(ref: RefObject<HTMLElement | null>): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry?.isIntersecting ?? true), { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export function useReducedMotion(): boolean {
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  return reduced;
}

/** The document's title while the page is shown. */
export function useTitle(title: string | undefined) {
  useEffect(() => {
    if (title) document.title = `${title} · OpenCloudSecurity`;
  }, [title]);
}
